import React, { useState, useEffect } from "react";
import { useUserAuth } from "../../context/userAuthContext";
import { db } from "../../firebase";
import {
    collection,
    query,
    getDocs,
    where,
    orderBy,
    limit,
} from "firebase/firestore";
import {
    ShoppingBag,
    ChevronDown,
    Search,
    Filter,
    ChevronLeft,
    ChevronRight,
    Calendar,
    Clock,
    Package,
    Truck,
    CheckCircle,
    AlertTriangle,
    XCircle,
    FileText,
    Download,
    RefreshCw,
    MoreHorizontal,
    Printer,
    Eye,
    ExternalLink,
    Building,
} from "lucide-react";

const Orders = () => {
    const { user } = useUserAuth();
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [dateRange, setDateRange] = useState({
        from: "",
        to: "",
    });
    const [selectedRestaurant, setSelectedRestaurant] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [ordersPerPage] = useState(10);
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        key: "createdAt",
        direction: "desc",
    });

    // Mock data for demonstration purposes
    useEffect(() => {
        const fetchData = async () => {
            try {
                // This would be replaced with your actual Firebase queries
                // Fetch restaurants owned by the user
                const mockRestaurants = [
                    { id: "rest1", name: "La Buena Mesa - Centro" },
                    { id: "rest2", name: "La Buena Mesa - Polanco" },
                    { id: "rest3", name: "La Buena Mesa - Santa Fe" },
                    { id: "rest4", name: "Café del Chef - Condesa" },
                ];

                // Fetch orders from all user's restaurants
                const mockOrders = [
                    {
                        id: "ORD-8762",
                        restaurantId: "rest1",
                        restaurantName: "La Buena Mesa - Centro",
                        supplierId: "sup1",
                        supplierName: "Distribuidora González",
                        orderNumber: "8762",
                        items: [
                            {
                                name: "Vino tinto reserva",
                                quantity: 24,
                                unit: "botellas",
                                pricePerUnit: 245,
                                totalPrice: 5880,
                            },
                            {
                                name: "Aceite de oliva extra virgen",
                                quantity: 10,
                                unit: "litros",
                                pricePerUnit: 180,
                                totalPrice: 1800,
                            },
                            {
                                name: "Queso manchego curado",
                                quantity: 5,
                                unit: "kg",
                                pricePerUnit: 320,
                                totalPrice: 1600,
                            },
                        ],
                        status: "entregado",
                        totalAmount: 9280,
                        createdAt: "2025-03-28T14:23:00",
                        updatedAt: "2025-04-01T15:45:00",
                        estimatedDelivery: "2025-04-01T14:00:00",
                        deliveredAt: "2025-04-01T13:42:00",
                        notes: "Entregar por la puerta trasera",
                        paymentStatus: "pagado",
                        invoiceNumber: "INV-2025-3421",
                    },
                    {
                        id: "ORD-8763",
                        restaurantId: "rest2",
                        restaurantName: "La Buena Mesa - Polanco",
                        supplierId: "sup2",
                        supplierName: "Carnes Premium",
                        orderNumber: "8763",
                        items: [
                            {
                                name: "Filete de res premium",
                                quantity: 12,
                                unit: "kg",
                                pricePerUnit: 450,
                                totalPrice: 5400,
                            },
                            {
                                name: "Pollo orgánico",
                                quantity: 15,
                                unit: "kg",
                                pricePerUnit: 120,
                                totalPrice: 1800,
                            },
                            {
                                name: "Cerdo ibérico",
                                quantity: 8,
                                unit: "kg",
                                pricePerUnit: 350,
                                totalPrice: 2800,
                            },
                        ],
                        status: "en camino",
                        totalAmount: 10000,
                        createdAt: "2025-04-01T09:15:00",
                        updatedAt: "2025-04-02T11:30:00",
                        estimatedDelivery: "2025-04-03T11:00:00",
                        notes: "Llamar 30 minutos antes de entrega",
                        paymentStatus: "pendiente",
                        invoiceNumber: "INV-2025-3422",
                    },
                    {
                        id: "ORD-8764",
                        restaurantId: "rest3",
                        restaurantName: "La Buena Mesa - Santa Fe",
                        supplierId: "sup3",
                        supplierName: "Frutas y Verduras S.A.",
                        orderNumber: "8764",
                        items: [
                            {
                                name: "Tomates orgánicos",
                                quantity: 20,
                                unit: "kg",
                                pricePerUnit: 45,
                                totalPrice: 900,
                            },
                            {
                                name: "Lechugas hidropónicas",
                                quantity: 30,
                                unit: "unidades",
                                pricePerUnit: 18,
                                totalPrice: 540,
                            },
                            {
                                name: "Aguacates Hass",
                                quantity: 15,
                                unit: "kg",
                                pricePerUnit: 85,
                                totalPrice: 1275,
                            },
                            {
                                name: "Limones",
                                quantity: 10,
                                unit: "kg",
                                pricePerUnit: 35,
                                totalPrice: 350,
                            },
                        ],
                        status: "procesando",
                        totalAmount: 3065,
                        createdAt: "2025-04-02T15:45:00",
                        updatedAt: "2025-04-02T16:20:00",
                        estimatedDelivery: "2025-04-04T09:00:00",
                        notes: "Producto para evento del fin de semana",
                        paymentStatus: "pendiente",
                        invoiceNumber: null,
                    },
                    {
                        id: "ORD-8765",
                        restaurantId: "rest4",
                        restaurantName: "Café del Chef - Condesa",
                        supplierId: "sup4",
                        supplierName: "Lácteos del Valle",
                        orderNumber: "8765",
                        items: [
                            {
                                name: "Leche entera",
                                quantity: 50,
                                unit: "litros",
                                pricePerUnit: 22,
                                totalPrice: 1100,
                            },
                            {
                                name: "Crema para batir",
                                quantity: 10,
                                unit: "litros",
                                pricePerUnit: 85,
                                totalPrice: 850,
                            },
                            {
                                name: "Queso crema",
                                quantity: 8,
                                unit: "kg",
                                pricePerUnit: 120,
                                totalPrice: 960,
                            },
                        ],
                        status: "entregado",
                        totalAmount: 2910,
                        createdAt: "2025-03-30T08:10:00",
                        updatedAt: "2025-04-01T09:45:00",
                        estimatedDelivery: "2025-04-01T09:00:00",
                        deliveredAt: "2025-04-01T08:50:00",
                        notes: "",
                        paymentStatus: "pagado",
                        invoiceNumber: "INV-2025-3420",
                    },
                    {
                        id: "ORD-8766",
                        restaurantId: "rest1",
                        restaurantName: "La Buena Mesa - Centro",
                        supplierId: "sup5",
                        supplierName: "Abarrotes Mayoreo",
                        orderNumber: "8766",
                        items: [
                            {
                                name: "Arroz premium",
                                quantity: 25,
                                unit: "kg",
                                pricePerUnit: 38,
                                totalPrice: 950,
                            },
                            {
                                name: "Frijoles negros",
                                quantity: 15,
                                unit: "kg",
                                pricePerUnit: 45,
                                totalPrice: 675,
                            },
                            {
                                name: "Pasta importada",
                                quantity: 20,
                                unit: "kg",
                                pricePerUnit: 55,
                                totalPrice: 1100,
                            },
                            {
                                name: "Azúcar refinada",
                                quantity: 20,
                                unit: "kg",
                                pricePerUnit: 25,
                                totalPrice: 500,
                            },
                        ],
                        status: "cancelado",
                        totalAmount: 3225,
                        createdAt: "2025-04-01T11:20:00",
                        updatedAt: "2025-04-01T14:30:00",
                        estimatedDelivery: null,
                        notes: "Pedido cancelado por cambio de menú",
                        paymentStatus: "reembolsado",
                        invoiceNumber: null,
                    },
                    {
                        id: "ORD-8767",
                        restaurantId: "rest2",
                        restaurantName: "La Buena Mesa - Polanco",
                        supplierId: "sup6",
                        supplierName: "Pescados y Mariscos del Puerto",
                        orderNumber: "8767",
                        items: [
                            {
                                name: "Camarones grandes",
                                quantity: 10,
                                unit: "kg",
                                pricePerUnit: 420,
                                totalPrice: 4200,
                            },
                            {
                                name: "Filete de salmón",
                                quantity: 8,
                                unit: "kg",
                                pricePerUnit: 380,
                                totalPrice: 3040,
                            },
                            {
                                name: "Pulpo",
                                quantity: 5,
                                unit: "kg",
                                pricePerUnit: 550,
                                totalPrice: 2750,
                            },
                        ],
                        status: "retrasado",
                        totalAmount: 9990,
                        createdAt: "2025-04-02T10:05:00",
                        updatedAt: "2025-04-03T08:45:00",
                        estimatedDelivery: "2025-04-03T14:00:00",
                        notes: "Problema con proveedor, entrega retrasada",
                        paymentStatus: "pendiente",
                        invoiceNumber: null,
                    },
                ];

                setRestaurants(mockRestaurants);
                setOrders(mockOrders);
                setFilteredOrders(mockOrders);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                setLoading(false);
            }
        };

        fetchData();
    }, [user]);

    // Filter orders based on search, status, date range, and restaurant
    useEffect(() => {
        let result = orders;

        // Filter by search term
        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            result = result.filter(
                (order) =>
                    order.orderNumber.toLowerCase().includes(lowercasedTerm) ||
                    order.supplierName.toLowerCase().includes(lowercasedTerm) ||
                    order.restaurantName.toLowerCase().includes(lowercasedTerm)
            );
        }

        // Filter by status
        if (statusFilter !== "all") {
            result = result.filter((order) => order.status === statusFilter);
        }

        // Filter by restaurant
        if (selectedRestaurant !== "all") {
            result = result.filter(
                (order) => order.restaurantId === selectedRestaurant
            );
        }

        // Filter by date range
        if (dateRange.from) {
            const fromDate = new Date(dateRange.from);
            result = result.filter(
                (order) => new Date(order.createdAt) >= fromDate
            );
        }
        if (dateRange.to) {
            const toDate = new Date(dateRange.to);
            toDate.setHours(23, 59, 59); // End of day
            result = result.filter(
                (order) => new Date(order.createdAt) <= toDate
            );
        }

        // Sort the filtered orders
        result = sortOrders(result);

        setFilteredOrders(result);
        setCurrentPage(1); // Reset to first page when filters change
    }, [orders, searchTerm, statusFilter, dateRange, selectedRestaurant, sortConfig]);

    // Sort orders based on sortConfig
    const sortOrders = (ordersToSort) => {
        return [...ordersToSort].sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === "asc" ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === "asc" ? 1 : -1;
            }
            return 0;
        });
    };

    // Request sort
    const requestSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    // Handle order expansion
    const toggleOrderExpansion = (orderId) => {
        if (expandedOrderId === orderId) {
            setExpandedOrderId(null);
        } else {
            setExpandedOrderId(orderId);
        }
    };

    // Get status badge class based on status
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case "entregado":
                return "bg-green-100 text-green-800";
            case "en camino":
                return "bg-blue-100 text-blue-800";
            case "procesando":
                return "bg-yellow-100 text-yellow-800";
            case "cancelado":
                return "bg-red-100 text-red-800";
            case "retrasado":
                return "bg-orange-100 text-orange-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    // Get status icon based on status
    const getStatusIcon = (status) => {
        switch (status) {
            case "entregado":
                return <CheckCircle className="h-4 w-4" />;
            case "en camino":
                return <Truck className="h-4 w-4" />;
            case "procesando":
                return <Package className="h-4 w-4" />;
            case "cancelado":
                return <XCircle className="h-4 w-4" />;
            case "retrasado":
                return <AlertTriangle className="h-4 w-4" />;
            default:
                return <Clock className="h-4 w-4" />;
        }
    };

    // Get payment status badge class
    const getPaymentBadgeClass = (status) => {
        switch (status) {
            case "pagado":
                return "bg-green-100 text-green-800";
            case "pendiente":
                return "bg-yellow-100 text-yellow-800";
            case "reembolsado":
                return "bg-blue-100 text-blue-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    // Get current orders for pagination
    const indexOfLastOrder = currentPage * ordersPerPage;
    const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
    const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Format date
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('es-MX', options);
    };

    // Format time
    const formatTime = (dateString) => {
        const options = { hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleTimeString('es-MX', options);
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
        }).format(amount);
    };

    // Handle export to CSV
    const handleExport = () => {
        alert("Función de exportación a implementar");
    };

    // Loading spinner component
    const LoadingSpinner = () => (
        <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pt-20 pb-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                        Gestión de Pedidos
                    </h1>
                    <p className="text-sm sm:text-base text-gray-600">
                        Visualiza y gestiona los pedidos de todos tus restaurantes
                    </p>
                </div>

                {/* Filters Section */}
                <div className="bg-white p-4 rounded-lg shadow mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Search */}
                        <div>
                            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                                Buscar
                            </label>
                            <div className="relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-4 w-4 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id="search"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                    placeholder="Orden, proveedor..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                                Estado
                            </label>
                            <select
                                id="status"
                                className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">Todos los estados</option>
                                <option value="procesando">Procesando</option>
                                <option value="en camino">En camino</option>
                                <option value="entregado">Entregado</option>
                                <option value="retrasado">Retrasado</option>
                                <option value="cancelado">Cancelado</option>
                            </select>
                        </div>

                        {/* Restaurant Filter */}
                        <div>
                            <label htmlFor="restaurant" className="block text-sm font-medium text-gray-700 mb-1">
                                Restaurante
                            </label>
                            <select
                                id="restaurant"
                                className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                value={selectedRestaurant}
                                onChange={(e) => setSelectedRestaurant(e.target.value)}
                            >
                                <option value="all">Todos los restaurantes</option>
                                {restaurants.map((restaurant) => (
                                    <option key={restaurant.id} value={restaurant.id}>
                                        {restaurant.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Date Range */}
                        <div>
                            <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700 mb-1">
                                Rango de fechas
                            </label>
                            <div className="flex space-x-2">
                                <input
                                    type="date"
                                    id="dateFrom"
                                    className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                    value={dateRange.from}
                                    onChange={(e) =>
                                        setDateRange({ ...dateRange, from: e.target.value })
                                    }
                                />
                                <input
                                    type="date"
                                    id="dateTo"
                                    className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                    value={dateRange.to}
                                    onChange={(e) =>
                                        setDateRange({ ...dateRange, to: e.target.value })
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 pt-4 border-t border-gray-200">
                        <div className="mb-2 sm:mb-0">
                            <p className="text-sm text-gray-700">
                                Mostrando <span className="font-medium">{filteredOrders.length}</span> pedidos
                            </p>
                        </div>
                        <div className="flex space-x-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchTerm("");
                                    setStatusFilter("all");
                                    setDateRange({ from: "", to: "" });
                                    setSelectedRestaurant("all");
                                }}
                                className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Limpiar filtros
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            >
                                <Download className="h-3 w-3 mr-1" />
                                Exportar
                            </button>
                        </div>
                    </div>
                </div>

                {/* Orders Table */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        {loading ? (
                            <LoadingSpinner />
                        ) : filteredOrders.length === 0 ? (
                            <div className="p-6 text-center">
                                <ShoppingBag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900">No hay pedidos</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    No se encontraron pedidos con los filtros seleccionados.
                                </p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort("orderNumber")}
                                        >
                                            <div className="flex items-center">
                                                <span>Orden #</span>
                                                {sortConfig.key === "orderNumber" && (
                                                    <span className="ml-1">
                                                        {sortConfig.direction === "asc" ? "▲" : "▼"}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort("restaurantName")}
                                        >
                                            <div className="flex items-center">
                                                <span>Restaurante</span>
                                                {sortConfig.key === "restaurantName" && (
                                                    <span className="ml-1">
                                                        {sortConfig.direction === "asc" ? "▲" : "▼"}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort("supplierName")}
                                        >
                                            <div className="flex items-center">
                                                <span>Proveedor</span>
                                                {sortConfig.key === "supplierName" && (
                                                    <span className="ml-1">
                                                        {sortConfig.direction === "asc" ? "▲" : "▼"}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort("createdAt")}
                                        >
                                            <div className="flex items-center">
                                                <span>Fecha</span>
                                                {sortConfig.key === "createdAt" && (
                                                    <span className="ml-1">
                                                        {sortConfig.direction === "asc" ? "▲" : "▼"}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort("status")}
                                        >
                                            <div className="flex items-center">
                                                <span>Estado</span>
                                                {sortConfig.key === "status" && (
                                                    <span className="ml-1">
                                                        {sortConfig.direction === "asc" ? "▲" : "▼"}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            onClick={() => requestSort("totalAmount")}
                                        >
                                            <div className="flex items-center">
                                                <span>Total</span>
                                                {sortConfig.key === "totalAmount" && (
                                                    <span className="ml-1">
                                                        {sortConfig.direction === "asc" ? "▲" : "▼"}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {currentOrders.map((order) => (
                                        <React.Fragment key={order.id}>
                                            <tr
                                                className={`hover:bg-gray-50 ${expandedOrderId === order.id ? "bg-purple-50" : ""
                                                    }`}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                                                            order.status
                                                        )}`}
                                                    >
                                                        {getStatusIcon(order.status)}
                                                        <span className="ml-1 capitalize">{order.status}</span>
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {formatCurrency(order.totalAmount)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="flex space-x-2">
                                                        <button
                                                            type="button"
                                                            className="text-purple-600 hover:text-purple-900"
                                                            title="Ver detalles"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="text-gray-600 hover:text-gray-900"
                                                            title="Imprimir pedido"
                                                        >
                                                            <Printer className="h-4 w-4" />
                                                        </button>
                                                        <div className="relative">
                                                            <button
                                                                type="button"
                                                                className="text-gray-600 hover:text-gray-900"
                                                                title="Más opciones"
                                                            >
                                                                <MoreHorizontal className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            {expandedOrderId === order.id && (
                                                <tr>
                                                    <td colSpan="7" className="px-6 py-4 bg-purple-50 border-t border-b border-purple-100">
                                                        <div className="text-sm">
                                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                                <div>
                                                                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                                                                        <Calendar className="h-4 w-4 mr-1 text-purple-500" />
                                                                        Información de entrega
                                                                    </h4>
                                                                    <div className="space-y-1 text-gray-700">
                                                                        <p>
                                                                            <span className="text-gray-500">Creado:</span>{" "}
                                                                            {formatDate(order.createdAt)} {formatTime(order.createdAt)}
                                                                        </p>
                                                                        <p>
                                                                            <span className="text-gray-500">Entrega estimada:</span>{" "}
                                                                            {order.estimatedDelivery
                                                                                ? `${formatDate(order.estimatedDelivery)} ${formatTime(
                                                                                    order.estimatedDelivery
                                                                                )}`
                                                                                : "No disponible"}
                                                                        </p>
                                                                        {order.deliveredAt && (
                                                                            <p>
                                                                                <span className="text-gray-500">Entregado:</span>{" "}
                                                                                {formatDate(order.deliveredAt)}{" "}
                                                                                {formatTime(order.deliveredAt)}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                                                                        <FileText className="h-4 w-4 mr-1 text-purple-500" />
                                                                        Información de pago
                                                                    </h4>
                                                                    <div className="space-y-1 text-gray-700">
                                                                        <p>
                                                                            <span className="text-gray-500">Estado:</span>{" "}
                                                                            <span
                                                                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPaymentBadgeClass(
                                                                                    order.paymentStatus
                                                                                )}`}
                                                                            >
                                                                                {order.paymentStatus}
                                                                            </span>
                                                                        </p>
                                                                        <p>
                                                                            <span className="text-gray-500">Factura:</span>{" "}
                                                                            {order.invoiceNumber || "No disponible"}
                                                                        </p>
                                                                        <p>
                                                                            <span className="text-gray-500">Total:</span>{" "}
                                                                            {formatCurrency(order.totalAmount)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                                                                        <Package className="h-4 w-4 mr-1 text-purple-500" />
                                                                        Notas
                                                                    </h4>
                                                                    <p className="text-gray-700">
                                                                        {order.notes || "Sin notas adicionales"}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div>
                                                                <h4 className="font-medium text-gray-900 mb-2">Productos</h4>
                                                                <div className="bg-white rounded-md border border-gray-200">
                                                                    <table className="min-w-full divide-y divide-gray-200">
                                                                        <thead className="bg-gray-50">
                                                                            <tr>
                                                                                <th
                                                                                    scope="col"
                                                                                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                                >
                                                                                    Producto
                                                                                </th>
                                                                                <th
                                                                                    scope="col"
                                                                                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                                >
                                                                                    Cantidad
                                                                                </th>
                                                                                <th
                                                                                    scope="col"
                                                                                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                                >
                                                                                    Precio unitario
                                                                                </th>
                                                                                <th
                                                                                    scope="col"
                                                                                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                                                >
                                                                                    Total
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                                            {order.items.map((item, idx) => (
                                                                                <tr key={idx} className="hover:bg-gray-50">
                                                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                                                        {item.name}
                                                                                    </td>
                                                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                                                                        {item.quantity} {item.unit}
                                                                                    </td>
                                                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                                                                        {formatCurrency(item.pricePerUnit)}
                                                                                    </td>
                                                                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                                        {formatCurrency(item.totalPrice)}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                        <tfoot className="bg-gray-50">
                                                                            <tr>
                                                                                <td colSpan="3" className="px-4 py-2 text-sm text-right font-medium text-gray-900">
                                                                                    Total del pedido:
                                                                                </td>
                                                                                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                                    {formatCurrency(order.totalAmount)}
                                                                                </td>
                                                                            </tr>
                                                                        </tfoot>
                                                                    </table>
                                                                </div>
                                                            </div>

                                                            <div className="mt-4 flex justify-end space-x-3">
                                                                <button
                                                                    type="button"
                                                                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                                                >
                                                                    <Printer className="h-4 w-4 mr-1" />
                                                                    Imprimir
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                                                >
                                                                    <ExternalLink className="h-4 w-4 mr-1" />
                                                                    Ver completo
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {filteredOrders.length > 0 && (
                        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Mostrando <span className="font-medium">{indexOfFirstOrder + 1}</span> a{" "}
                                        <span className="font-medium">
                                            {Math.min(indexOfLastOrder, filteredOrders.length)}
                                        </span>{" "}
                                        de <span className="font-medium">{filteredOrders.length}</span> resultados
                                    </p>
                                </div>
                                <div>
                                    <nav
                                        className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                        aria-label="Pagination"
                                    >
                                        <button
                                            onClick={() => paginate(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1
                                                ? "text-gray-300 cursor-not-allowed"
                                                : "text-gray-500 hover:bg-gray-50"
                                                }`}
                                        >
                                            <span className="sr-only">Anterior</span>
                                            <ChevronLeft className="h-5 w-5" />
                                        </button>
                                        {Array.from(
                                            { length: Math.ceil(filteredOrders.length / ordersPerPage) },
                                            (_, i) => i + 1
                                        ).map((number) => (
                                            <button
                                                key={number}
                                                onClick={() => paginate(number)}
                                                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === number
                                                    ? "z-10 bg-purple-50 border-purple-500 text-purple-600"
                                                    : "text-gray-500 hover:bg-gray-50"
                                                    }`}
                                            >
                                                {number}
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => paginate(currentPage + 1)}
                                            disabled={
                                                currentPage ===
                                                Math.ceil(filteredOrders.length / ordersPerPage)
                                            }
                                            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage ===
                                                Math.ceil(filteredOrders.length / ordersPerPage)
                                                ? "text-gray-300 cursor-not-allowed"
                                                : "text-gray-500 hover:bg-gray-50"
                                                }`}
                                        >
                                            <span className="sr-only">Siguiente</span>
                                            <ChevronRight className="h-5 w-5" />
                                        </button>
                                    </nav>
                                </div>
                            </div>

                            <div className="flex sm:hidden justify-between w-full">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium rounded-md ${currentPage === 1
                                        ? "text-gray-300 cursor-not-allowed"
                                        : "text-gray-700 hover:bg-gray-50"
                                        }`}
                                >
                                    Anterior
                                </button>
                                <p className="text-sm text-gray-700">
                                    <span className="font-medium">{currentPage}</span> de{" "}
                                    <span className="font-medium">
                                        {Math.ceil(filteredOrders.length / ordersPerPage)}
                                    </span>
                                </p>
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={
                                        currentPage ===
                                        Math.ceil(filteredOrders.length / ordersPerPage)
                                    }
                                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium rounded-md ${currentPage ===
                                        Math.ceil(filteredOrders.length / ordersPerPage)
                                        ? "text-gray-300 cursor-not-allowed"
                                        : "text-gray-700 hover:bg-gray-50"
                                        }`}
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Help Card */}
                <div className="mt-8 bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h3 className="text-sm font-medium text-purple-800 mb-2">
                        Gestión de pedidos
                    </h3>
                    <p className="text-sm text-purple-700 mb-3">
                        Consejos para gestionar tus pedidos eficientemente:
                    </p>
                    <ul className="text-sm text-purple-700 space-y-1 ml-4">
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>Utiliza los filtros para encontrar rápidamente pedidos específicos</span>
                        </li>
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>Exporta tus pedidos para análisis o reportes contables</span>
                        </li>
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>Revisa regularmente pedidos con estado "retrasado" o "procesando"</span>
                        </li>
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>Haz clic en el número de orden para ver detalles completos</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default Orders;